<template>
  <!-- One file in the composer: what it is doing now, and how to remove or retry it -->
  <div
    class="file-chip"
    :class="`file-chip--${state.tone}`"
    v-tooltip="state.tooltip"
  >
    <img
      v-if="entry.preview_url && entry.status === 'uploading'"
      class="file-thumb"
      :src="entry.preview_url"
      alt=""
    />
    <status-spinner
      v-else-if="state.tone === 'busy'"
      :size="16"
      class="file-icon"
    />
    <i
      v-else-if="state.tone === 'ok'"
      class="fas fa-check file-icon file-icon--success"
    ></i>
    <i
      v-else-if="state.tone === 'part'"
      class="fas fa-exclamation-triangle file-icon file-icon--warning"
    ></i>
    <i v-else class="fas fa-times-circle file-icon file-icon--danger"></i>

    <div class="file-info">
      <span class="file-name">{{ truncateFileName(name, 24) }}</span>
      <span class="file-status" :class="`file-status--${state.tone}`">{{
        state.label
      }}</span>
    </div>

    <button
      v-if="state.tone === 'bad'"
      class="retry-file-button"
      :aria-label="`Retry ${name}`"
      title="Retry"
      @click="$emit('retry')"
    >
      <i class="fas fa-redo"></i>
    </button>
    <button
      class="remove-file-button"
      :aria-label="`Remove ${name}`"
      title="Remove file"
      @click="$emit('remove')"
    >
      <i class="fas fa-times"></i>
    </button>

    <div v-if="state.progress !== null" class="file-progress">
      <span :style="{ width: `${Math.round(state.progress * 100)}%` }"></span>
    </div>
  </div>
</template>

<script>
import StatusSpinner from "@/components/OliverBotV2/shared/StatusSpinner.vue";
import { chipState } from "@/components/OliverBotV2/attachments/attachmentStatus.js";
import { truncateFileName } from "@/utils/fileHelpers.js";

export default {
  name: "AttachmentChip",
  components: { StatusSpinner },
  props: {
    entry: {
      type: Object,
      required: true,
    },
  },
  emits: ["remove", "retry"],
  computed: {
    state() {
      return chipState(this.entry);
    },
    name() {
      return this.entry.original_file_name || "File";
    },
  },
  methods: {
    truncateFileName,
  },
};
</script>

<style lang="scss" scoped>
.file-chip {
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: var(--oliver-bg, white);
  border: 1px solid var(--oliver-border, rgba(0, 0, 0, 0.1));
  border-radius: 8px;
  min-width: 200px;
  max-width: 280px;
  flex-shrink: 0;
  animation: slideIn 0.2s ease;
  transition: border-color 0.2s ease, background 0.2s ease;

  &--ok {
    border-color: rgba(72, 187, 120, 0.4);
  }

  &--part {
    border-color: rgba(237, 137, 54, 0.4);
    background: rgba(237, 137, 54, 0.04);
  }

  &--bad {
    border-color: rgba(229, 62, 62, 0.4);
    background: rgba(229, 62, 62, 0.04);
  }

  &--busy {
    border-color: rgba(99, 179, 237, 0.4);
    background: rgba(99, 179, 237, 0.04);
  }
}

.file-thumb {
  width: 28px;
  height: 28px;
  border-radius: 5px;
  object-fit: cover;
  flex-shrink: 0;
}

.file-icon {
  font-size: 16px;
  color: var(--oliver-accent);
  flex-shrink: 0;
}

.file-icon--success {
  color: #48bb78;
}

.file-icon--warning {
  color: #ed8936;
}

.file-icon--danger {
  color: #e53e3e;
}

.file-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.file-name {
  font-size: 13px;
  font-weight: 500;
  color: var(--oliver-text, #1a1a1a);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.file-status {
  font-size: 11px;
  color: var(--oliver-text-muted, #999);
  font-variant-numeric: tabular-nums;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &--part {
    color: #c05621;
  }

  &--bad {
    color: #e53e3e;
  }
}

.file-progress {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 3px;
  background: rgba(99, 179, 237, 0.15);

  span {
    display: block;
    height: 100%;
    background: var(--oliver-accent);
    border-radius: 0 2px 2px 0;
    transition: width 0.2s ease;
  }
}

.retry-file-button {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  border: none;
  background: rgba(99, 179, 237, 0.15);
  color: #3182ce;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.2s ease;

  i {
    font-size: 10px;
  }

  &:hover {
    background: rgba(49, 130, 206, 0.2);
  }
}

.remove-file-button {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: none;
  background: rgba(0, 0, 0, 0.05);
  color: var(--oliver-text-muted, #666);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.2s ease;

  i {
    font-size: 10px;
    line-height: 12px;
  }

  &:hover,
  &:focus-visible {
    background: rgba(229, 62, 62, 0.1);
    color: #e53e3e;
  }
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: scale(0.9);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

@media (prefers-reduced-motion: reduce) {
  .file-chip {
    animation: none;
  }
}

@media (max-width: 768px) {
  .file-chip {
    min-width: 180px;
    max-width: 240px;
    padding: 6px 10px;
  }

  .file-name {
    font-size: 12px;
  }

  .file-status {
    font-size: 10px;
  }
}
</style>
